import { environment } from "src/environments/environment";
import { BaseJob, JobTypeLabels, StopTypeEnum } from "../job.type";
import { AnyJobBase } from "../any-job-base";
import { BuildServices, RouteEventAction, RouteEventActionColorMap, RouteEventActionFillMap, RouteEventActionTypeMap } from ".";
import * as helpers from '../../helpers/ui-helpers';
import { Capacitor } from "@capacitor/core";

export class GroupTripMonitor extends AnyJobBase {

  public value: BaseJob;
  public onTheWayCompleted = !environment.features.hasOnTheWay;
  public label: string;
  public _for: string = this.translateSvc.translate('LABEL.for');
  public riderName: string;
  public type = 'group-trip-monitor';

  constructor(value: BaseJob, services: BuildServices) {
    super(value, services);
    this.headerInfo.type = 'LABEL.groupTripMonitor',
    this.label = 'LABEL.groupTripMonitor';
    this.actions = this.getActions();
    this.typeIcons = [ helpers.TypeIcons.groupTripMonitor ];
    this.getActions();
    this.updateTimeBasedProperties();
  }

  public getActions(): RouteEventAction[] {
    const { arriveTime } = this.value;
    const label = this.translateSvc.translate(this.value.stopType === StopTypeEnum.MONITOR_PICKUP ? JobTypeLabels[1] : JobTypeLabels[2]);
    const actions: RouteEventAction[] =  [
      {
        type: RouteEventActionTypeMap.onTheWay,
        label: 'ACTION.onTheWay',
        show: helpers.canOnTheWayJob(this.value),
        icon: 'send',
        color: RouteEventActionColorMap.primary,
        fill: RouteEventActionFillMap.solid,
        preview: false,
        drivingMode: false,
        disabled: false,
        fn: (action: RouteEventAction) => this.onTheWay(action),
      },
      {
        type: RouteEventActionTypeMap.arrive,
        label: `${ this.translateSvc.translate('ACTION.arrive') } ${ this.translateSvc.translate(this.label) }`,
        show: helpers.canArrive(this.value),
        icon: 'locate', color: RouteEventActionColorMap.primary, fill: RouteEventActionFillMap.solid,
        preview: false,
        drivingMode: false,
        disabled: false,
        fn: (action: RouteEventAction) => this.arrive({ action }),
      },
      {
        type: RouteEventActionTypeMap.depart,
        label: `${ this.translateSvc.translate('ACTION.depart') } ${ this.translateSvc.translate(this.label) }`,
        show: environment.features.hasDepart && !!this.value.arriveTime,
        icon: 'log-out', color: RouteEventActionColorMap.primary, fill: RouteEventActionFillMap.solid,
        preview: false,
        drivingMode: false,
        disabled: false,
        fn: (action: RouteEventAction) => this.depart({ action }),
      },
      {
        type: RouteEventActionTypeMap.navigate,
        label: `ACTION.navigate`,
        show: this.value.jobEnabled && helpers.hasValidGeo(this.location),
        icon: 'navigate',
        color: RouteEventActionColorMap.primary,
        fill: RouteEventActionFillMap.outline,
        preview: false,
        drivingMode: true,
        disabled: false,
        fn: async () => this.locationSvc.navigateToDestination(this.location.coordinate),
      },
      {
        type: RouteEventActionTypeMap.seeOn,
        label: `${ this.translateSvc.translate('LABEL.seeOn') } ${ this.translateSvc.translate('LABEL.map') }`,
        show: (this.appData.currentRouteIsPreview || Capacitor.getPlatform() !== 'ios') && helpers.hasValidGeo(this.location),
        icon: 'map', color: RouteEventActionColorMap.medium,
        fill: RouteEventActionFillMap.outline,
        preview: true,
        drivingMode: false,
        disabled: false,
        fn: async () => this.locationSvc.previewLocation(this.location.coordinate, this.location.name),
      },
    ].filter(action => action.show);
    return actions;
  }

  public updateTimeBasedProperties = () => {
    const timeToDiff = this.scheduledStart;
    this.isLate = this.completed ? false : helpers.isInThePast(timeToDiff);
    const actions = this.getActions();
    if (JSON.stringify(actions) !== JSON.stringify(this.actions)) {
      this.actions = this.getActions();
    }
    this.headerInfo.timeTo = this.scheduledStart?.toRelative();
  };

  public arrive = async ({ action, checkLocation = false, checkHasArrived = true }) => {
    try {
      if (action) action.disabled = true;
      const hasArrived = checkHasArrived ? await this.locationSvc.checkHasArrived([ this ]) : true;
      if (!hasArrived) { return; }
      await this.dispatchSvc.arriveJobs([ this ]);
      this.value.arriveTime = Date.now().toString();
      this.actions = this.getActions();
      this.appData.setJobs([ this.value ]);
    } catch (error) {
      this.notificationSvc.error(error.message || error);
    } finally {
      if (action) action.disabled = false;
    }
  };

  public depart = async ({ action }) => {
    try {
      if (action) action.disabled = true;
      await this.dispatchSvc.departJobs([ this ]);
      this.value.departedTime = Date.now().toString();
      this.value.completed = true;
      this.actions = this.getActions();
      this.appData.setJobs([ this.value ]);
    } catch (error) {
      this.notificationSvc.error(error.message || error);
    } finally {
      if (action) action.disabled = false;
    }
  };

}
